import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button, CircularProgress,
  Snackbar, Alert, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, FormControl, InputLabel, Select, MenuItem, Tooltip, Pagination,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Payment as PaymentIcon,
  CheckCircle as CompensateIcon,
  Block as VoidIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../../config/environment';
import { usePageBreakpoints, pageContainerSx } from '../../hooks/useClinicPageBreakpoints';


interface CheckIssued {
  id: string;
  payee_name: string;
  bank_account_id?: string;
  check_number: string;
  amount: number;
  issue_date: string;
  due_date: string;
  status: string;
  notes?: string;
  compensation_date?: string;
  void_reason?: string;
  created_at: string;
}

const statusLabels: Record<string, string> = {
  pending: 'Pendente',
  compensated: 'Compensado',
  cancelled: 'Cancelado',
  voided: 'Anulado',
};

const statusColors: Record<string, string> = {
  pending: '#f59e0b',
  compensated: '#22c55e',
  cancelled: '#ef4444',
  voided: '#9ca3af',
};

const formatCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const formatDate = (d: string) =>
  d ? new Date(d).toLocaleDateString('pt-BR') : '-';

const emptyForm = {
  payee_name: '', bank_account_id: '', check_number: '', amount: '',
  issue_date: '', due_date: '', notes: '',
};

const ChecksIssued: React.FC = () => {
  const { isMobile } = usePageBreakpoints();

  const [checks, setChecks] = useState<CheckIssued[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CheckIssued | null>(null);
  const [form, setForm] = useState<typeof emptyForm>({ ...emptyForm });
  const [saving, setSaving] = useState(false);

  const [compensateDialog, setCompensateDialog] = useState(false);
  const [compensateTarget, setCompensateTarget] = useState<string | null>(null);

  const [voidDialog, setVoidDialog] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voidTarget, setVoidTarget] = useState<string | null>(null);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  useEffect(() => {
    loadChecks();
  }, [page, search, filterStatus, dateFrom, dateTo]);

  const loadChecks = async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, limit };
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await axios.get(getApiUrl('/financial-module/checks-issued'), { params });
      setChecks(res.data?.data || []);
      setTotal(res.data?.total || 0);
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Erro ao carregar cheques', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setDialogOpen(true);
  };

  const openEdit = (check: CheckIssued) => {
    setEditing(check);
    setForm({
      payee_name: check.payee_name || '',
      bank_account_id: check.bank_account_id || '',
      check_number: check.check_number || '',
      amount: String(check.amount || ''),
      issue_date: check.issue_date ? check.issue_date.split('T')[0] : '',
      due_date: check.due_date ? check.due_date.split('T')[0] : '',
      notes: check.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.payee_name || !form.check_number || !form.amount) {
      setSnackbar({ open: true, message: 'Beneficiário, Nº do cheque e Valor são obrigatórios', severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount) };
      if (editing) {
        await axios.put(getApiUrl(`/financial-module/checks-issued/${editing.id}`), payload);
        setSnackbar({ open: true, message: 'Cheque atualizado com sucesso', severity: 'success' });
      } else {
        await axios.post(getApiUrl('/financial-module/checks-issued'), payload);
        setSnackbar({ open: true, message: 'Cheque registrado com sucesso', severity: 'success' });
      }
      setDialogOpen(false);
      loadChecks();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Erro ao salvar cheque', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este cheque?')) return;
    try {
      await axios.delete(getApiUrl(`/financial-module/checks-issued/${id}`));
      setSnackbar({ open: true, message: 'Cheque excluído com sucesso', severity: 'success' });
      loadChecks();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Erro ao excluir cheque', severity: 'error' });
    }
  };

  const handleCompensate = async () => {
    if (!compensateTarget) return;
    try {
      await axios.post(getApiUrl(`/financial-module/checks-issued/${compensateTarget}/compensate`));
      setSnackbar({ open: true, message: 'Cheque compensado com sucesso', severity: 'success' });
      setCompensateDialog(false);
      setCompensateTarget(null);
      loadChecks();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Erro ao compensar cheque', severity: 'error' });
    }
  };

  const handleVoid = async () => {
    if (!voidTarget) return;
    try {
      await axios.post(getApiUrl(`/financial-module/checks-issued/${voidTarget}/void`), { void_reason: voidReason });
      setSnackbar({ open: true, message: 'Cheque anulado com sucesso', severity: 'success' });
      setVoidDialog(false);
      setVoidTarget(null);
      setVoidReason('');
      loadChecks();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Erro ao anular cheque', severity: 'error' });
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <Box sx={pageContainerSx}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaymentIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Cheques Emitidos</Typography>
            <Typography variant="body2" color="text.secondary">Gestão de cheques emitidos para fornecedores e terceiros</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Atualizar">
            <IconButton onClick={loadChecks} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Novo Cheque
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField fullWidth size="small" placeholder="Buscar beneficiário, cheque..."
              value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment> }} />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={filterStatus} label="Status" onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="pending">Pendente</MenuItem>
                <MenuItem value="compensated">Compensado</MenuItem>
                <MenuItem value="cancelled">Cancelado</MenuItem>
                <MenuItem value="voided">Anulado</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" label="Data Início" type="date" value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" label="Data Fim" type="date" value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }} InputLabelProps={{ shrink: true }} />
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }} elevation={1}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700 }}>Beneficiário</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Nº Cheque</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Valor</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Emissão</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Vencimento</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : checks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: '#94a3b8' }}>
                    Nenhum cheque encontrado.
                  </TableCell>
                </TableRow>
              ) : (
                checks.map((check) => (
                  <TableRow key={check.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{check.payee_name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{check.check_number}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(check.amount)}</Typography>
                    </TableCell>
                    <TableCell>{formatDate(check.issue_date)}</TableCell>
                    <TableCell>{formatDate(check.due_date)}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={statusLabels[check.status] || check.status}
                        size="small"
                        sx={{
                          bgcolor: `${statusColors[check.status] || '#9ca3af'}18`,
                          color: statusColors[check.status] || '#9ca3af',
                          fontWeight: 600, fontSize: '0.75rem',
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        {check.status === 'pending' && (
                          <>
                            <Tooltip title="Compensar">
                              <IconButton size="small" sx={{ color: '#22c55e' }}
                                onClick={() => { setCompensateTarget(check.id); setCompensateDialog(true); }}>
                                <CompensateIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Anular">
                              <IconButton size="small" sx={{ color: '#9ca3af' }}
                                onClick={() => { setVoidTarget(check.id); setVoidReason(''); setVoidDialog(true); }}>
                                <VoidIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => openEdit(check)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton size="small" onClick={() => handleDelete(check.id)} sx={{ color: '#ef4444' }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} color="primary" />
          </Box>
        )}
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog fullScreen={isMobile} scroll="paper" open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f8fafc' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaymentIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {editing ? 'Editar Cheque Emitido' : 'Novo Cheque Emitido'}
            </Typography>
          </Box>
          <IconButton onClick={() => setDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Beneficiário *" value={form.payee_name}
                onChange={(e) => setForm({ ...form, payee_name: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="ID da Conta Bancária (opcional)" value={form.bank_account_id}
                onChange={(e) => setForm({ ...form, bank_account_id: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Nº Cheque *" value={form.check_number}
                onChange={(e) => setForm({ ...form, check_number: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Valor (R$) *" type="number" value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                inputProps={{ step: 0.01, min: 0 }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Data Emissão" type="date" value={form.issue_date}
                onChange={(e) => setForm({ ...form, issue_date: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Data Vencimento" type="date" value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Observações" value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} multiline rows={2} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            startIcon={saving ? <CircularProgress size={18} /> : undefined}
            sx={{ textTransform: 'none', fontWeight: 600 }}>
            {saving ? 'Salvando...' : editing ? 'Atualizar' : 'Registrar Cheque'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Compensate Dialog */}
      <Dialog fullScreen={isMobile} scroll="paper" open={compensateDialog} onClose={() => setCompensateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f8fafc' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CompensateIcon sx={{ color: '#22c55e' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Compensar Cheque</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Confirma a compensação deste cheque emitido?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCompensateDialog(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleCompensate}
            sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' } }}>
            Confirmar Compensação
          </Button>
        </DialogActions>
      </Dialog>

      {/* Void Dialog */}
      <Dialog fullScreen={isMobile} scroll="paper" open={voidDialog} onClose={() => setVoidDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f8fafc' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VoidIcon sx={{ color: '#9ca3af' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Anular Cheque</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField fullWidth size="small" label="Motivo da Anulação" value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
            multiline rows={2} placeholder="Informe o motivo da anulação..." sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setVoidDialog(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleVoid}
            sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#78716c', '&:hover': { bgcolor: '#57534e' } }}>
            Confirmar Anulação
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChecksIssued;
